import { formatValue } from './fieldValues'

const TEXT_TYPES = ['TEXT', 'TEXTAREA', 'EMAIL', 'PHONE', 'URL']
const RANGE_TYPES = ['NUMBER', 'DECIMAL', 'CURRENCY', 'ROLLUP', 'DATE', 'DATETIME']

export const OP_LABELS = { eq: 'is', contains: 'contains', gte: '≥', lte: '≤' }

/** Operators the backend accepts per field type (RecordService.addFilter). */
export const operatorsFor = (field) => {
  if (field.fieldType === 'LOOKUP' || field.fieldType === 'USER') return ['eq']
  if (TEXT_TYPES.includes(field.fieldType)) return ['contains', 'eq']
  if (RANGE_TYPES.includes(field.fieldType)) return ['eq', 'gte', 'lte']
  return ['eq']
}

export const isSortable = (field) => !['MULTI_SELECT', 'LOOKUP', 'USER'].includes(field.fieldType)

export const DEFAULT_SORT = 'updatedOn,desc'
export const DEFAULT_COLUMN_COUNT = 5

export const emptyListState = () => ({ columns: null, filters: {}, sort: DEFAULT_SORT, q: '', layout: 'table', groupBy: null, sumField: null })

/** Fields a board can be grouped by (one column per option) and summed by. */
export const isGroupable = (field) => field.fieldType === 'SELECT' || field.fieldType === 'RADIO'
/** The parent-link field of a record type that links to itself (a tree), if any. */
export const treeFieldOf = (fields, entityType) =>
  fields.find((f) => f.fieldType === 'LOOKUP' && f.parentLink && f.lookupEntityType === entityType) || null

export const isSummable = (field) => ['NUMBER', 'DECIMAL', 'CURRENCY', 'ROLLUP'].includes(field.fieldType)

/**
 * A saved config may mention fields removed from the form since: drop them instead of
 * sending them to the backend (which would reject the whole query).
 */
export const sanitizeConfig = (config, fields) => {
  const byKey = Object.fromEntries(fields.map((f) => [f.fieldKey, f]))
  const filters = {}
  Object.entries(config?.filters || {}).forEach(([key, ops]) => {
    const field = byKey[key]
    if (!field || !ops || typeof ops !== 'object') return
    const allowed = operatorsFor(field)
    const kept = Object.fromEntries(Object.entries(ops).filter(([op, v]) => allowed.includes(op) && v !== '' && v != null))
    if (Object.keys(kept).length) filters[key] = kept
  })
  const columns = Array.isArray(config?.columns) ? config.columns.filter((k) => byKey[k] && !byKey[k].hidden) : null
  let sort = typeof config?.sort === 'string' ? config.sort : DEFAULT_SORT
  if (sort.startsWith('data.')) {
    const field = byKey[sort.slice(5).split(',')[0]]
    if (!field || !isSortable(field)) sort = DEFAULT_SORT
  } else if (!/^(createdOn|updatedOn)(,(asc|desc))?$/.test(sort)) {
    sort = DEFAULT_SORT
  }
  const groupBy = byKey[config?.groupBy] && isGroupable(byKey[config.groupBy]) ? config.groupBy : null
  const sumField = byKey[config?.sumField] && isSummable(byKey[config.sumField]) ? config.sumField : null
  return {
    columns: columns && columns.length ? columns : null,
    filters,
    sort,
    q: typeof config?.q === 'string' ? config.q : '',
    // a board view whose grouping field disappeared falls back to the table (same for a tree)
    layout: config?.layout === 'board' && groupBy ? 'board' : config?.layout === 'tree' && fields.some((f) => f.parentLink) ? 'tree' : 'table',
    groupBy,
    sumField,
  }
}

/** Stable comparison of two list states (has the selected view been modified?). */
export const sameState = (a, b) => JSON.stringify(normalize(a)) === JSON.stringify(normalize(b))

const normalize = (s) => ({
  columns: s.columns || null,
  filters: Object.keys(s.filters).sort().map((k) => [k, Object.keys(s.filters[k]).sort().map((op) => [op, String(s.filters[k][op])])]),
  sort: s.sort,
  q: (s.q || '').trim(),
  layout: s.layout || 'table',
  groupBy: s.groupBy || null,
  sumField: s.sumField || null,
})

export const filterCount = (filters) => Object.values(filters).reduce((n, ops) => n + Object.keys(ops).length, 0)

/** "Score ≥ 10", "Source is Web", "Tags includes Hot" */
export const describeFilter = (field, op, value) => {
  const shown = op === 'eq' && field.fieldType === 'CHECKBOX'
    ? (value === 'true' ? 'Yes' : 'No')
    : formatValue(field, field.fieldType === 'MULTI_SELECT' ? [value] : value)
  const label = field.fieldType === 'MULTI_SELECT' && op === 'eq' ? 'includes' : OP_LABELS[op]
  return `${field.label} ${label} ${shown}`
}
